import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { ProfileFormComponent } from '../../../../components/forms/settingsforms/profile-form/profile-form.component';
import { ChangePasswordFormComponent } from '../../../../components/forms/settingsforms/profile-form/change-password-form/change-password-form.component';
import { SettingsService } from '../../../../services/settings/settings.service';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { IRole, TBlkCxpRole } from '../../../../types/settingsDataModule';
import {
  CAPABILITY_GROUPS,
  describeRole,
  formatRoleName,
  visibleRoles,
} from '../settings-roles';

interface AccessRole {
  name: string;
  label: string;
  description: string;
}

interface AccessGroup {
  title: string;
  items: { label: string; allowed: boolean }[];
  allowedCount: number;
}

@Component({
  selector: 'app-profile-settings',
  standalone: true,
  imports: [CommonModule, ProfileFormComponent, ChangePasswordFormComponent],
  templateUrl: './profile-settings.component.html',
  styleUrl: './profile-settings.component.css',
})
export class ProfileSettingsComponent implements OnInit {
  private settingsService = inject(SettingsService);
  private permissionsService = inject(PermissionsService);
  private destroyRef = inject(DestroyRef);

  roles: AccessRole[] = [];
  groups: AccessGroup[] = [];
  /** False until the profile has said which roles this person holds. */
  rolesKnown = false;

  ngOnInit(): void {
    // Role descriptions come from IAM when another page has already loaded
    // them; otherwise the built-in summaries are used. No extra request.
    combineLatest([
      this.settingsService.getUserRoles,
      this.settingsService.rolesData,
      this.settingsService.getUserProfileData,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([userRoles, iamRoles, profile]: [TBlkCxpRole[], IRole[], unknown]) => {
        this.rolesKnown = (userRoles || []).length > 0 || !!profile;
        this.roles = visibleRoles(userRoles).map((name) => ({
          name,
          label: formatRoleName(name),
          description: describeRole(name, iamRoles),
        }));
        this.groups = CAPABILITY_GROUPS.map((group) => {
          const items = group.items.map((item) => ({
            label: item.label,
            allowed: this.permissionsService.checkPermission(
              userRoles,
              item.category,
              item.permission
            ),
          }));
          return {
            title: group.title,
            items,
            allowedCount: items.filter((item) => item.allowed).length,
          };
        });
      });
  }
}
